/**
 * The empty state for an account with no projects yet.
 *
 * Real generations shipped with Dunea, shown prompt-first: the sentence someone
 * actually typed, then what came out and how long it took. For a first-time
 * user the prompt is the useful half — it is the only honest answer to "how
 * much detail do I need to give this thing?" — and clicking one loads it into
 * the composer to edit rather than firing a build of somebody else's business.
 *
 * Provenance: these are Dunea's own demo builds, already public on dunea.ai.
 * None belongs to a customer. Keep it that way — never surface a paying
 * account's site here. Times marked measured come from `build_history`;
 * Respawn and KitchenFix predate that table and are owner estimates. Both sets
 * are mirrored from `website/src/app/home-client.tsx`; update the two together.
 */
const SHOWCASE = [
  {
    id: 'pencilco',
    // Typed exactly like this, typo included. That is the point: a five-page
    // site out of a careless one-liner.
    prompt: 'build me a webiste for the pencil company multipage',
    name: 'The Pencil Company',
    meta: '5 pages',
    buildTime: '6 min',
    url: 'https://webiste-pencil.dunea.app',
  },
  {
    id: 'respawn',
    prompt:
      "Make a landing page for a gaming café called Respawn — dark and neon, big bold type, session booking, events, and a 'book a station' flow.",
    name: 'Respawn',
    meta: 'Landing page',
    buildTime: '4 min',
    url: 'https://gaming.dunea.app',
  },
  {
    id: 'velamarina',
    prompt:
      'Build a complete multi-page site for Vela Marina, a luxury seaside hotel — rooms & suites, dining, spa, experiences, gallery, and contact, each with its own booking or enquiry flow. Dark, elegant and editorial, with scroll animations and real photography throughout.',
    name: 'Vela Marina',
    meta: '7 pages',
    buildTime: '20 min',
    url: 'https://velamarina.dunea.app',
  },
];

export function ShowcasePrompts({ onUsePrompt }: { onUsePrompt: (prompt: string) => void }) {
  return (
    <div className="relative z-10 mt-12 w-full sm:mt-14">
      <div className="mb-1 flex items-baseline gap-2.5 border-t border-hairline pt-5">
        <span className="ui-caption">Built with Dunea</span>
        <div className="h-px flex-1" />
        <span className="font-geist text-[11.5px] text-faint">real prompts, real build times</span>
      </div>

      <ul className="m-0 list-none p-0">
        {SHOWCASE.map((item) => (
          <li key={item.id} className="border-b border-hairline last:border-b-0">
            <div className="group flex flex-col gap-2 py-3.5 sm:flex-row sm:items-baseline sm:gap-5">
              <button
                type="button"
                onClick={() => onUsePrompt(item.prompt)}
                title="Put this prompt in the composer"
                className="min-w-0 flex-1 cursor-pointer border-0 bg-transparent p-0 text-left"
              >
                {/* Geist, not the display serif: this is body copy people
                    actually read, and Instrument Serif is hard work below
                    display size. The serif is reserved for the hero headline. */}
                <span className="block font-geist text-[13px] leading-[1.6] text-secondary transition-colors group-hover:text-primary sm:text-[13.5px]">
                  “{item.prompt}”
                </span>
              </button>

              <div className="flex flex-shrink-0 items-center gap-2 sm:w-[13rem] sm:flex-col sm:items-end sm:gap-0.5">
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap font-geist text-[12.5px] font-medium text-muted no-underline transition-colors hover:text-accent"
                >
                  {item.name} ↗
                </a>
                {/* Mono for the numbers only — it's the figures that want to be
                    scanned, and tracked-out mono caps on the name was one more
                    thing to decode. */}
                <span className="whitespace-nowrap font-geist-mono text-[11px] text-faint">
                  {item.meta} · {item.buildTime}
                </span>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
